/**
 * Firestore REST API wrapper for iOS workaround
 * 
 * On iOS, Firestore SDK doesn't work because auth.currentUser is not set.
 * This utility provides REST API alternatives for all Firestore operations.
 */

import { Capacitor } from '@capacitor/core';
import { getTokens, isTokenExpired, updateTokens } from './tokenStorage';
import { refreshIdToken } from './firebaseAuthRestApi';
import { auth } from '../firebase/config';

const PROJECT_ID = 'waka-running';
const FIRESTORE_BASE_URL = `https://firestore.googleapis.com/v1/projects/${PROJECT_ID}/databases/(default)/documents`;

/**
 * Get the stored idToken, refreshing if expired.
 * Falls back to Firebase Auth SDK getIdToken() so REST calls never fail
 * just because Capacitor Preferences are stale.
 */
async function getIdToken(): Promise<string | null> {
  try {
    const tokens = await getTokens();

    if (tokens) {
      const expired = await isTokenExpired();

      if (!expired) {
        return tokens.idToken;
      }

      // Token expired — try REST refresh first (avoids SDK hang risk)
      if (tokens.refreshToken) {
        console.log('[FirestoreRestAPI] Token expired, refreshing via REST...');
        try {
          const refreshResponse = await refreshIdToken(tokens.refreshToken);
          const newIdToken = refreshResponse.id_token;
          const newRefreshToken = refreshResponse.refresh_token || tokens.refreshToken;
          await updateTokens(newIdToken, newRefreshToken);
          console.log('[FirestoreRestAPI] Token refreshed successfully');
          return newIdToken;
        } catch (refreshError: any) {
          console.warn('[FirestoreRestAPI] REST token refresh failed, trying SDK fallback:', refreshError.message);
          if (refreshError.message === 'REFRESH_TOKEN_EXPIRED') {
            const { clearTokens } = await import('./tokenStorage');
            await clearTokens();
          }
          // Fall through to SDK fallback below
        }
      }
    }

    // Fallback: ask Firebase Auth SDK for the current token.
    // getIdToken() on an already-authenticated user is fast (uses cached token or a quick refresh).
    // This covers the case where Capacitor Preferences are empty/stale but the user IS signed in.
    const currentUser = auth.currentUser;
    if (currentUser) {
      console.log('[FirestoreRestAPI] Using Firebase Auth SDK getIdToken() as fallback');
      try {
        const sdkToken = await Promise.race([
          currentUser.getIdToken(false),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('SDK getIdToken timeout')), 8000)),
        ]);
        return sdkToken;
      } catch (sdkErr) {
        console.warn('[FirestoreRestAPI] SDK getIdToken fallback failed:', sdkErr);
      }
    }

    console.warn('[FirestoreRestAPI] No valid auth token available');
    return null;
  } catch (e) {
    console.warn('Could not get idToken:', e);
    return null;
  }
}

/**
 * Make a Firestore REST API request
 */
async function firestoreRequest(
  method: string,
  path: string,
  body?: any
): Promise<any> {
  const idToken = await getIdToken();
  if (!idToken) {
    throw new Error('No authentication token available. Please sign in again.');
  }

  const url = `${FIRESTORE_BASE_URL}/${path}`;
  const headers: HeadersInit = {
    'Authorization': `Bearer ${idToken}`,
    'Content-Type': 'application/json',
  };

  // Add timeout to prevent hanging
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  const options: RequestInit = {
    method,
    headers,
    signal: controller.signal,
  };

  if (body && method !== 'GET') {
    options.body = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, options);
    clearTimeout(timeoutId);
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Firestore request timed out after 10 seconds');
    }
    throw error;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(`Firestore REST API error: ${response.status} ${response.statusText}`);
  }

  return await response.json();
}

/**
 * Convert Firestore REST API document to our data format
 */
function convertFirestoreDocument(doc: any): any {
  if (!doc.fields) return null;

  const data: any = {};
  for (const [key, value] of Object.entries(doc.fields)) {
    const field = value as any;
    let fieldValue: any = null;

    if (field.stringValue !== undefined) {
      fieldValue = field.stringValue;
    } else if (field.integerValue !== undefined) {
      fieldValue = parseInt(field.integerValue);
    } else if (field.doubleValue !== undefined) {
      fieldValue = parseFloat(field.doubleValue);
    } else if (field.booleanValue !== undefined) {
      fieldValue = field.booleanValue;
    } else if (field.timestampValue !== undefined) {
      // Convert ISO timestamp string to Timestamp object
      const date = new Date(field.timestampValue);
      // Return as object with seconds and nanoseconds for compatibility
      fieldValue = {
        seconds: Math.floor(date.getTime() / 1000),
        nanoseconds: (date.getTime() % 1000) * 1000000,
        toDate: () => date,
        toMillis: () => date.getTime()
      };
    } else if (field.nullValue !== undefined) {
      fieldValue = null;
    } else if (field.arrayValue !== undefined) {
      // Handle arrays — items can be any Firestore type including nested objects
      fieldValue = (field.arrayValue.values || []).map((v: any) => {
        if (v.stringValue !== undefined) return v.stringValue;
        if (v.integerValue !== undefined) return parseInt(v.integerValue);
        if (v.doubleValue !== undefined) return parseFloat(v.doubleValue);
        if (v.booleanValue !== undefined) return v.booleanValue;
        if (v.nullValue !== undefined) return null;
        if (v.timestampValue !== undefined) {
          const date = new Date(v.timestampValue);
          return {
            seconds: Math.floor(date.getTime() / 1000),
            nanoseconds: (date.getTime() % 1000) * 1000000,
            toDate: () => date,
            toMillis: () => date.getTime()
          };
        }
        if (v.mapValue !== undefined) {
          return convertFirestoreDocument({ fields: v.mapValue.fields || {} });
        }
        if (v.arrayValue !== undefined) {
          return (v.arrayValue.values || []).map((inner: any) =>
            inner.stringValue ?? inner.integerValue ?? inner.booleanValue ?? null
          );
        }
        return null;
      });
    } else if (field.mapValue !== undefined) {
      // Handle nested objects (like profilePrivacy)
      fieldValue = convertFirestoreDocument({ fields: field.mapValue.fields || {} });
    } else {
      console.warn(`Unknown field type for ${key}:`, field);
      fieldValue = null;
    }

    data[key] = fieldValue;
  }

  return data;
}

/**
 * Convert a JavaScript value to Firestore REST API format
 */
function convertToFirestoreValue(value: any): any {
  if (value === null || value === undefined) {
    return { nullValue: null };
  }
  
  if (typeof value === 'string') {
    return { stringValue: value };
  }
  
  if (typeof value === 'number') {
    if (Number.isInteger(value)) {
      return { integerValue: String(value) };
    } else {
      return { doubleValue: value };
    }
  }
  
  if (typeof value === 'boolean') {
    return { booleanValue: value };
  }
  
  if (value instanceof Date) {
    return { timestampValue: value.toISOString() };
  }
  
  // Handle Firestore Timestamp-like objects
  if (value && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return { timestampValue: value.toDate().toISOString() };
  }
  
  // Handle objects (convert to mapValue)
  if (typeof value === 'object' && !Array.isArray(value)) {
    const fields: any = {};
    for (const [key, val] of Object.entries(value)) {
      fields[key] = convertToFirestoreValue(val);
    }
    return { mapValue: { fields } };
  }
  
  // Handle arrays
  if (Array.isArray(value)) {
    return {
      arrayValue: {
        values: value.map(v => convertToFirestoreValue(v))
      }
    };
  }
  
  // Fallback to string
  return { stringValue: String(value) };
}

/**
 * Firestore REST API service
 */
export const firestoreRestApi = {
  /**
   * Get a single document
   */
  async getDocument(collection: string, docId: string): Promise<any> {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('No authentication token available. Please sign in again.');
    }

    const path = `${collection}/${docId}`;
    const url = `${FIRESTORE_BASE_URL}/${path}`;
    
    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Firestore get request timed out after 10 seconds');
      }
      throw error;
    }

    // Handle 404 (document not found) - return null like Firebase SDK does
    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Firestore REST API getDocument error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(`Firestore REST API error: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.fields) {
      return null;
    }

    const data = convertFirestoreDocument(result);
    return {
      id: docId,
      ...data,
      exists: () => true,
      data: () => data,
    };
  },

  /**
   * Create a document (returns the document ID)
   */
  async createDocument(collection: string, data: any): Promise<string> {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('No authentication token available. Please sign in again.');
    }

    // Convert data to Firestore REST API format
    const fields: any = {};
    for (const [key, value] of Object.entries(data)) {
      // Skip undefined values
      if (value === undefined || value === null) {
        continue;
      }
      
      // Check if this is a serverTimestamp() placeholder
      // Firestore SDK's serverTimestamp() returns an object with _methodName
      if (typeof value === 'object' && value !== null && ('_methodName' in value || (value as any)._delegate?.methodName === 'serverTimestamp')) {
        // Use current timestamp (Firestore REST API doesn't support server-side timestamps directly)
        // In production, you might want to omit this field and use updateMask, but for now use current time
        fields[key] = { timestampValue: new Date().toISOString() };
      } else {
        fields[key] = convertToFirestoreValue(value);
      }
    }

    const url = `${FIRESTORE_BASE_URL}/${collection}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fields }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Firestore REST API create error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Firestore create error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      // Extract document ID from the response name
      // Format: projects/{project}/databases/(default)/documents/{collection}/{docId}
      const docId = result.name.split('/').pop();
      return docId;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Firestore create request timed out after 10 seconds');
      }
      throw error;
    }
  },

  /**
   * Set a document with a specific ID (creates or overwrites)
   * Use merge=true to update only specific fields without overwriting the entire document
   */
  async setDocument(collection: string, docId: string, data: any, merge: boolean = false): Promise<void> {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('No authentication token available. Please sign in again.');
    }

    // Convert data to Firestore REST API format
    const fields: any = {};
    const fieldPaths: string[] = [];
    for (const [key, value] of Object.entries(data)) {
      // Skip undefined values
      if (value === undefined || value === null) {
        continue;
      }
      
      // Skip 'id' field - it's not stored in Firestore document
      if (key === 'id') {
        continue;
      }
      
      fieldPaths.push(key); // Track which fields we're updating
      
      // Check if this is a serverTimestamp() placeholder
      if (typeof value === 'object' && value !== null && ('_methodName' in value || (value as any)._delegate?.methodName === 'serverTimestamp')) {
        fields[key] = { timestampValue: new Date().toISOString() };
      } else {
        fields[key] = convertToFirestoreValue(value);
      }
    }

    // Build URL with updateMask if merge is true
    let url = `${FIRESTORE_BASE_URL}/${collection}/${docId}`;
    if (merge && fieldPaths.length > 0) {
      // Add updateMask to only update specific fields
      const updateMaskParams = fieldPaths.map(path => `updateMask.fieldPaths=${path}`).join('&');
      url += `?${updateMaskParams}`;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      console.log('[FirestoreRestAPI] Setting document at:', url, merge ? '(merge mode)' : '(overwrite mode)');
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fields }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Firestore REST API setDocument error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Firestore set error: ${response.status} ${response.statusText}`);
      }

      console.log('[FirestoreRestAPI] Document set successfully');
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Firestore set request timed out after 10 seconds');
      }
      throw error;
    }
  },

  /**
   * Delete a document
   */
  async deleteDocument(collection: string, docId: string): Promise<void> {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('No authentication token available. Please sign in again.');
    }

    const url = `${FIRESTORE_BASE_URL}/${collection}/${docId}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`,
        },
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Firestore REST API deleteDocument error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(`Firestore delete error: ${response.status} ${response.statusText}`);
      }
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Firestore delete request timed out after 10 seconds');
      }
      throw error;
    }
  },

  /**
   * Query documents (simplified - supports basic where clauses)
   */
  async queryDocuments(
    collection: string,
    filters?: Array<{ field: string; op: string; value: any }>,
    limitCount?: number,
    orderByField?: { fieldPath: string; direction?: 'ASCENDING' | 'DESCENDING' }
  ): Promise<any[]> {
    // Firestore REST API uses runQuery endpoint for queries
    const queryUrl = `${FIRESTORE_BASE_URL}:runQuery`;

    const structuredQuery: any = {
      from: [{ collectionId: collection }],
    };

    // Add where clauses
    if (filters && filters.length > 0) {
      if (filters.length === 1) {
        // Single filter - use fieldFilter directly
        const filter = filters[0];
        let value: any = {};
        
        // Determine value type
        if (typeof filter.value === 'string') {
          value = { stringValue: filter.value };
        } else if (typeof filter.value === 'number') {
          if (Number.isInteger(filter.value)) {
            value = { integerValue: String(filter.value) };
          } else {
            value = { doubleValue: filter.value };
          }
        } else if (typeof filter.value === 'boolean') {
          value = { booleanValue: filter.value };
        } else {
          value = { stringValue: String(filter.value) };
        }
        
        structuredQuery.where = {
          fieldFilter: {
            field: { fieldPath: filter.field },
            op: filter.op === '==' ? 'EQUAL' : filter.op,
            value: value,
          },
        };
      } else {
        // Multiple filters - use compositeFilter
        structuredQuery.where = {
          compositeFilter: {
            op: 'AND',
            filters: filters.map(filter => {
              let value: any = {};
              
              if (typeof filter.value === 'string') {
                value = { stringValue: filter.value };
              } else if (typeof filter.value === 'number') {
                if (Number.isInteger(filter.value)) {
                  value = { integerValue: String(filter.value) };
                } else {
                  value = { doubleValue: filter.value };
                }
              } else if (typeof filter.value === 'boolean') {
                value = { booleanValue: filter.value };
              } else {
                value = { stringValue: String(filter.value) };
              }
              
              return {
                fieldFilter: {
                  field: { fieldPath: filter.field },
                  op: filter.op === '==' ? 'EQUAL' : filter.op,
                  value: value,
                },
              };
            }),
          },
        };
      }
    }

    // Add limit
    if (limitCount) {
      structuredQuery.limit = limitCount;
    }

    if (orderByField?.fieldPath) {
      structuredQuery.orderBy = [
        {
          field: { fieldPath: orderByField.fieldPath },
          direction: orderByField.direction || 'ASCENDING',
        },
      ];
    }

    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('No authentication token available. Please sign in again.');
    }

    // Add timeout to prevent hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    let response: Response;
    try {
      response = await fetch(queryUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ structuredQuery }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Firestore query timed out after 10 seconds');
      }
      throw error;
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Firestore REST API query error:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      throw new Error(`Firestore query error: ${response.status} ${response.statusText}`);
    }

    const results = await response.json();
    
    // Handle empty results or non-array responses
    if (!Array.isArray(results)) {
      console.warn('Firestore REST API returned non-array result:', results);
      return [];
    }
    
    // Convert results
    const documents: any[] = [];
    for (const result of results) {
      if (result.document) {
        try {
          const docId = result.document.name.split('/').pop();
          const data = convertFirestoreDocument(result.document);
          documents.push({
            id: docId,
            ...data,
            exists: () => true,
            data: () => data,
          });
        } catch (error) {
          console.warn('Error converting Firestore document:', error, result);
        }
      }
    }

    return documents;
  },

  /**
   * Check if we should use REST API (iOS platform or Android if needed)
   */
  shouldUseRestApi(): boolean {
    const platform = Capacitor.getPlatform();
    return platform === 'ios' || platform === 'android';
  },
};

